import React from "react";
import { Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import styled from "styled-components";

import { WidgetTheme } from "../../types/analytics";
import { HoursWatchedFormat, formatRawDurationValue } from "../../utils/analytics";
import WidgetInfoButton from "./WidgetInfoButton";

export type OperationalMetricCardItem = {
    key: string;
    label: string;
    value: number | null;
    changePercent?: number | null;
    valueFormat?: "number" | "percent" | "durationHours" | "numberCompactMillionsWithTooltip" | "durationHoursCompactMillionsWithTooltip";
    description?: string;
    sparkline?: number[];
    sparklineAverage?: number[];
};

type Props = {
    metrics: OperationalMetricCardItem[];
    theme: WidgetTheme;
    hoursWatchedFormat?: HoursWatchedFormat;
    emptyMessage?: string;
    minCardWidth?: string;
    maxCardWidth?: string;
    gridGap?: string;
};

const MetricCardsGrid = styled.div<{ $minCardWidth: string; $maxCardWidth: string; $gridGap: string }>`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, ${props => props.$minCardWidth}), ${props => props.$maxCardWidth}));
    gap: ${props => props.$gridGap};
    align-items: stretch;
    justify-content: start;
`;

const MetricCard = styled.div<{ theme: WidgetTheme }>`
    position: relative;
    border: 1px solid ${props => props.theme.border};
    border-radius: 8px;
    padding: 16px;
    background: ${props => props.theme.chartBg};
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
`;

const MetricLabel = styled.div<{ theme: WidgetTheme }>`
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 0.9rem;
    color: ${props => props.theme.text};
    opacity: 0.7;
    margin-bottom: 8px;
`;

const MetricValue = styled.div<{ theme: WidgetTheme }>`
    font-size: 1.8rem;
    font-weight: 700;
    line-height: 1.1;
    color: ${props => props.theme.text};
`;

const MetricChange = styled.div<{ $positive: boolean; $negative: boolean; $positiveColor: string; $negativeColor: string }>`
    margin-top: 8px;
    font-size: 0.95rem;
    font-weight: 600;
    color: ${props => props.$positive ? props.$positiveColor : props.$negative ? props.$negativeColor : "#6c757d"};
`;

const COMPACT_THRESHOLD = 10_000_000;

const formatCompactMillions = (value: number): string => `${(value / 1_000_000).toFixed(2)}m`;

const formatFullValueTooltip = (value: number, suffix?: string): string => `${value.toLocaleString()}${suffix || ""}`;

const formatNumber = (
    value: number | null,
    format: "number" | "percent" | "durationHours" | "numberCompactMillionsWithTooltip" | "durationHoursCompactMillionsWithTooltip",
    hoursWatchedFormat: HoursWatchedFormat = "duration"
): string => {
    if (value === null || value === undefined) return "-";
    if (format === "percent") return `${(value * 100).toFixed(2)}%`;
    if (format === "durationHours") {
        return hoursWatchedFormat === "raw" ? formatRawDurationValue(value) : `${value.toFixed(1)}h`;
    }
    if (format === "numberCompactMillionsWithTooltip" || format === "durationHoursCompactMillionsWithTooltip") {
        if (format === "durationHoursCompactMillionsWithTooltip" && hoursWatchedFormat === "raw") {
            return formatRawDurationValue(value);
        }

        return Math.abs(value) >= COMPACT_THRESHOLD ? formatCompactMillions(value) : format === "durationHoursCompactMillionsWithTooltip" ? `${value.toFixed(1)}h` : value.toLocaleString();
    }
    return value.toLocaleString();
};

const renderMetricValue = (metric: OperationalMetricCardItem, theme: WidgetTheme, hoursWatchedFormat: HoursWatchedFormat): React.ReactNode => {
    const format = metric.valueFormat || "number";
    const value = metric.value;
    const formattedValue = formatNumber(value, format, hoursWatchedFormat);

    if (
        value !== null
        && value !== undefined
        && Math.abs(value) >= COMPACT_THRESHOLD
        && (format === "numberCompactMillionsWithTooltip" || format === "durationHoursCompactMillionsWithTooltip")
        && !(format === "durationHoursCompactMillionsWithTooltip" && hoursWatchedFormat === "raw")
    ) {
        const tooltipText = format === "durationHoursCompactMillionsWithTooltip"
            ? formatFullValueTooltip(value, "h")
            : formatFullValueTooltip(value);

        return (
            <OverlayTrigger
                placement="top"
                overlay={<Tooltip id={`metric-tooltip-${metric.key}`}>{tooltipText}</Tooltip>}
            >
                <span style={{ cursor: "help", borderBottom: `1px dotted ${theme.text}` }}>{formattedValue}</span>
            </OverlayTrigger>
        );
    }

    return formattedValue;
};

const formatSignedPct = (value: number | null): string => {
    if (value === null || value === undefined) return "—";
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
};

const renderSparkline = (series: number[] = [], averageSeries: number[] = []): React.ReactNode => {
    const width = 180;
    const height = 44;
    if (!series.length) return <div style={{ fontSize: "12px", opacity: 0.6 }}>No sparkline</div>;
    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = max - min || 1;
    const points = series
        .map((v, i) => {
            const x = (i / Math.max(series.length - 1, 1)) * (width - 4) + 2;
            const y = height - 2 - ((v - min) / range) * (height - 6);
            return `${x},${y}`;
        })
        .join(" ");

    const avgPoints = averageSeries.length
        ? averageSeries
            .map((v, i) => {
                const x = (i / Math.max(averageSeries.length - 1, 1)) * (width - 4) + 2;
                const y = height - 2 - ((v - min) / range) * (height - 6);
                return `${x},${y}`;
            })
            .join(" ")
        : "";

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
            {avgPoints ? (
                <polyline fill="none" stroke="#c0c4cc" strokeWidth="1.5" strokeDasharray="3 3" points={avgPoints} />
            ) : null}
            <polyline fill="none" stroke="#00acc1" strokeWidth="2" points={points} />
        </svg>
    );
};

const OperationalMetricCards: React.FC<Props> = ({
    metrics,
    theme,
    hoursWatchedFormat = "duration",
    emptyMessage = "No KPI data available.",
    minCardWidth = "180px",
    maxCardWidth = "200px",
    gridGap = "16px"
}) => {
    if (metrics.length === 0) return <Alert variant="info">{emptyMessage}</Alert>;

    return (
        <MetricCardsGrid $minCardWidth={minCardWidth} $maxCardWidth={maxCardWidth} $gridGap={gridGap}>
            {metrics.map((metric) => {
                const trendValue = metric.changePercent ?? 0;
                const positive = trendValue > 0;
                const negative = trendValue < 0;

                return (
                    <MetricCard key={metric.key} theme={theme} className="w-100 h-100" style={{ minHeight: "190px" }}>
                        {metric.description && (
                            <div style={{ position: "absolute", top: "8px", right: "8px", zIndex: 1 }}>
                                <WidgetInfoButton description={metric.description} placement="auto" />
                            </div>
                        )}
                        <MetricLabel theme={theme}>
                            <span>{metric.label}</span>
                        </MetricLabel>
                        <MetricValue theme={theme}>{renderMetricValue(metric, theme, hoursWatchedFormat)}</MetricValue>
                        <MetricChange $positive={positive} $negative={negative} $positiveColor={theme.positiveColor} $negativeColor={theme.negativeColor}>
                            {formatSignedPct(metric.changePercent ?? null)}
                        </MetricChange>
                        <div style={{ marginTop: "8px", display: "flex", justifyContent: "center" }}>
                            {renderSparkline(metric.sparkline || [], metric.sparklineAverage || [])}
                        </div>
                    </MetricCard>
                );
            })}
        </MetricCardsGrid>
    );
};

export default OperationalMetricCards;